import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { FirstLoginLanding, InvitationScreen } from '../components';
import { useAcceptInvitation } from '../hooks/useAcceptInvitation.js';
import { useInvitation } from '../hooks/useInvitation.js';
import {
  forgetConfirmation,
  hasRememberedConfirmation,
  rememberConfirmation,
} from '../utils/pendingConfirmation.js';

/**
 * Route container of an invitation link: loads the invitation, runs the login-before-accept
 * sequence and renders the presentational screens. Public: it needs no session to open. Opening
 * the link never counts as consent: only pressing Accept does (remembered for the sign-in trip).
 *
 * @param {object} props
 * @param {boolean} props.isAuthenticated Session state, provided by the host.
 * @param {boolean} props.authLoading The host is still resolving the session.
 * @param {string} [props.loginPath='/login'] Host sign-in route; it receives `?next=` to come back.
 * @param {string|null} [props.signedInEmail] Email of the session, when the host knows it: a link meant for another address asks to sign out first.
 * @param {() => void} [props.onSignOut] Host sign-out, for that case.
 */
function AcceptInvitationPage({ isAuthenticated, authLoading, loginPath = '/login', signedInEmail = null, onSignOut }) {
  const { token } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { invitation, linkState, loading, error: lookupError, retry } = useInvitation(token, {
    signedInEmail: isAuthenticated ? signedInEmail : null,
  });

  // Read once on arrival: true only when this very token was confirmed before going to sign in.
  const [resumeConfirm] = useState(() => hasRememberedConfirmation(token));
  useEffect(() => {
    forgetConfirmation();
  }, []);

  const handleSignInRequired = useCallback(() => {
    rememberConfirmation(token);
    navigate(`${loginPath}?next=${encodeURIComponent(pathname)}`);
  }, [navigate, token, pathname, loginPath]);

  const { confirm, confirming, accepted, error } = useAcceptInvitation({
    token,
    linkState,
    isAuthenticated,
    authLoading,
    resumeConfirm,
    onSignInRequired: handleSignInRequired,
  });

  const handleOpenStudio = useCallback(
    () => navigate(accepted.firstStudio.path),
    [navigate, accepted],
  );

  const handleStay = useCallback(() => navigate('/'), [navigate]);

  if (accepted) {
    return (
      <FirstLoginLanding
        workspaceName={accepted.workspaceName}
        studioName={accepted.firstStudio?.name}
        onOpen={handleOpenStudio}
      />
    );
  }

  return (
    <InvitationScreen
      // Until the session is known, who the link is for cannot be compared: no Accept yet.
      loading={loading || authLoading}
      error={lookupError}
      linkState={linkState}
      invitation={invitation}
      onConfirm={confirm}
      confirming={confirming}
      hasAcceptError={Boolean(error)}
      signedInEmail={signedInEmail}
      onSignOut={onSignOut}
      onStay={handleStay}
      onRetry={retry}
    />
  );
}

export default AcceptInvitationPage;
